import { mutate } from 'swr';
import { toast } from 'sonner';
import { useGenerationPolling, type PollingResponseContext, type UseMediaPollingParams } from '@/components/nodes/shared/use-generation-polling';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';
import { notifyAssetLibraryChanged } from '@/components/canvas/utils/asset-folders-events';

export type UseAudioPollingParams = UseMediaPollingParams;

function handleResponse(
    s: { status: string; outputUrl?: string; outputUrls?: string[]; type?: string; lyrics?: string },
    ctx: PollingResponseContext,
): 'break' | 'continue' {
    if (s.status === 'completed' && s.outputUrl) {
        const nowIso = new Date().toISOString();
        const ld = ctx.getLatestData();
        const urls = Array.isArray(s.outputUrls) && s.outputUrls.length > 1
            ? s.outputUrls.filter(Boolean)
            : [s.outputUrl];
        const hasMulti = urls.length > 1;
        const mimeType = s.type || 'audio/mpeg';
        const { history, activeHistoryId } = reuseOrAppendHistory(
            ld?.history,
            urls as string[],
            (id) => ({
                id,
                url: urls[0]!,
                ...(hasMulti ? { urls } : {}),
                type: mimeType,
                lyrics: s.lyrics,
                description: '',
                createdAt: nowIso,
                prompt: ld?.instructions ?? '',
                modelId: ctx.modelId,
            }),
        );
        try {
            ctx.updateNodeData(ctx.nodeId, {
                updatedAt: nowIso,
                status: 'completed',
                model: ctx.modelId,
                generated: hasMulti
                    ? { url: urls[0]!, urls, type: mimeType, lyrics: s.lyrics }
                    : { url: urls[0]!, type: mimeType, lyrics: s.lyrics },
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                history,
                activeHistoryId,
            });
        } catch {}
        try { toast.success(ctx.t('Audio generated successfully')); } catch {}
        notifyAssetLibraryChanged();
        setTimeout(() => mutate(ctx.projectId ? `credits:${ctx.projectId}` : 'credits'), 5000);
        return 'break';
    }

    if (s.status === 'failed') {
        try {
            ctx.updateNodeData(ctx.nodeId, {
                status: 'failed',
                updatedAt: new Date().toISOString(),
                error: 'Audio generation failed',
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
            });
        } catch {}
        return 'break';
    }

    return 'continue';
}

export function useAudioPolling(params: UseAudioPollingParams) {
    useGenerationPolling({
        ...params,
        statusEndpoint: '/api/speech/status',
        handleResponse,
    });
}